import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, SparklesIcon, ArrowLeftIcon } from 'lucide-react';
export function EnrichmentHero() {
  const [activeRow, setActiveRow] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveRow(prev => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  const businesses = [{
    name: 'The daily grind cafe',
    address: '123 Main St, NY',
    revenue: '$1.2M/yr',
    employees: '12 employees',
    techStack: 'Square, Toast POS'
  }, {
    name: 'Cityscape realty group',
    address: '456 Oak Ave, CA',
    revenue: '$4.8M/yr',
    employees: '35 employees',
    techStack: 'Salesforce, HubSpot'
  }, {
    name: 'Precision auto repair',
    address: '789 Pine Rd, TX',
    revenue: '$850K/yr',
    employees: '8 employees',
    techStack: 'Shopify, QuickBooks'
  }];
  return <section className="relative pt-16 pb-24 overflow-hidden bg-white" data-id="element-2763">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2764">
        <a href="#/features" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors mb-8" data-id="element-2765">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-2766" /> All Features
        </a>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2767">
          {/* Left Column: Copy & CTAs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-2xl" data-id="element-2768">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 mb-8 leading-[1.05]" data-id="element-2769">
              Enrich your <br data-id="element-2770" />
              <span className="text-blue-600" data-id="element-2771">local business</span> <br data-id="element-2772" />
              data
            </h1>

            <p className="text-xl sm:text-2xl text-slate-600 mb-8 max-w-lg leading-relaxed" data-id="element-2773">
              Enrich local business contacts with{' '}
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-blue-50 text-blue-700 text-lg sm:text-xl font-medium mx-1" data-id="element-2774">
                verified emails
              </span>
              ,{' '}
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-green-50 text-green-700 text-lg sm:text-xl font-medium mx-1" data-id="element-2775">
                business profiles
              </span>
              , and{' '}
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-purple-50 text-purple-700 text-lg sm:text-xl font-medium mx-1 mt-2 sm:mt-0" data-id="element-2776">
                tech stacks
              </span>
              . Turn basic records into actionable data.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-2777">
              <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="px-10 py-4 text-white font-bold bg-blue-600 rounded-full hover:bg-blue-700 transition-colors text-lg text-center" data-id="element-2778">
                Try for free
              </a>
            </div>

            {/* Static Stats Bar */}
            <div className="flex gap-12 pt-8" data-id="element-2779">
              <div data-id="element-2780">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-2781">
                  200M+
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-2782">
                  Local businesses
                </div>
              </div>
              <div data-id="element-2783">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-2784">
                  50+
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-2785">
                  Data points per business
                </div>
              </div>
              <div data-id="element-2786">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-2787">
                  Weekly
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-2788">
                  Data updates
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Live Data Preview Card */}
          <div className="relative" data-id="element-2789">
            <div className="bg-slate-50 rounded-[2rem] p-8 sm:p-10 relative z-10" data-id="element-2790">
              <div className="flex justify-between items-center mb-8" data-id="element-2791">
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-2792">
                  Enrichment preview
                </h3>
              </div>

              <div className="overflow-x-auto" data-id="element-2793">
                <table className="w-full text-sm text-left" data-id="element-2794">
                  <thead data-id="element-2795">
                    <tr className="text-slate-400 font-medium border-b border-slate-200/60" data-id="element-2796">
                      <th className="pb-4 pl-4 pr-4 font-medium" data-id="element-2797">
                        Input record
                      </th>
                      <th className="pb-4 px-4 font-medium" data-id="element-2798"></th>
                      <th className="pb-4 pl-4 font-medium" data-id="element-2799">
                        Enriched business intelligence
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200/60" data-id="element-2800">
                    {businesses.map((biz, idx) => <tr key={idx} className={`transition-all duration-500 relative ${activeRow === idx ? 'bg-white shadow-sm rounded-xl' : ''}`} data-id="element-2801">
                        <td className="py-6 pl-4 pr-4 font-medium text-slate-900 rounded-l-xl" data-id="element-2802">
                          <div data-id="element-2803">{biz.name}</div>
                          <div className="text-xs text-slate-500 font-normal mt-1" data-id="element-2804">
                            {biz.address}
                          </div>
                        </td>
                        <td className="py-6 px-4 text-slate-400" data-id="element-2805">
                          <ArrowRightIcon className="w-4 h-4" data-id="element-2806" />
                        </td>
                        <td className="py-6 pl-4 rounded-r-xl" data-id="element-2807">
                          <div className="flex flex-wrap gap-1.5" data-id="element-2808">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium" data-id="element-2809">
                              {biz.revenue}
                            </span>
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium" data-id="element-2810">
                              {biz.employees}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 mt-1.5" data-id="element-2811">
                            {biz.techStack}
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200/60 flex justify-end" data-id="element-2812">
                <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors" data-id="element-2813">
                  Export data
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}